/**
 * The page head's read time: when the page last got an answer. A failed reload
 * keeps the time of the last success; a new `scope` (the selected network)
 * starts over at "Not read yet", never showing another network's read. Pass
 * only sources that really answered: a result of failures alone is not a read.
 */

import { useEffect, useRef, useState } from "react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const two = (n: number): string => String(n).padStart(2, "0");

/** "00:05": hours and minutes, UTC. */
export function formatUtcTime(at: number | Date): string {
  const d = typeof at === "number" ? new Date(at) : at;
  return `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}`;
}

/** "Read 2 Oct 00:05 UTC", or "Not read yet" before the first answer. */
export function formatReadTime(at: number | Date | null): string {
  if (at === null) return "Not read yet";
  const d = typeof at === "number" ? new Date(at) : at;
  return `Read ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${formatUtcTime(d)} UTC`;
}

/** A moment in a status line: "00:05 UTC" on the day of `now` (UTC), else "1 Oct 14:10 UTC". */
export function formatUtcStamp(at: number | Date | string, now: number = Date.now()): string {
  const d = typeof at === "string" ? new Date(at) : typeof at === "number" ? new Date(at) : at;
  const today = new Date(now);
  const sameDay = d.getUTCFullYear() === today.getUTCFullYear() && d.getUTCMonth() === today.getUTCMonth() && d.getUTCDate() === today.getUTCDate();
  return sameDay ? `${formatUtcTime(d)} UTC` : `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${formatUtcTime(d)} UTC`;
}

/** The time (ms) a source last changed to a non-null value within `scope`, or null before any did. */
export function useReadTime(scope: string, ...sources: readonly unknown[]): number | null {
  const previous = useRef<{ scope: string; sources: readonly unknown[] }>({ scope, sources: [] });
  const [state, setState] = useState<{ scope: string; at: number | null }>({ scope, at: null });
  // No deps on purpose: compared on every render, stamped only on a change.
  useEffect(() => {
    const prev = previous.current.scope === scope ? previous.current.sources : [];
    const fresh = sources.some((s, i) => s !== null && s !== undefined && !Object.is(s, prev[i]));
    previous.current = { scope, sources };
    if (fresh) setState({ scope, at: Date.now() });
    else if (state.scope !== scope) setState({ scope, at: null });
  });
  return state.scope === scope ? state.at : null;
}
